import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  formatMoney,
  formatAmountCompact,
  formatAmountShort,
  parseAmountDigits,
  formatAmountInputDisplay,
  getProgress,
} from "@/lib/format";
import { getChildDisplay } from "@/lib/childDisplay";
import {
  PRESET_AMOUNTS,
  MIN_DONATE_AMOUNT,
  PAYMENT_PROVIDERS,
  DEFAULT_PROVIDER_ID,
  isInAppProvider,
  type ProviderId,
} from "@/constants";
import { payZoomradFee, ZoomradPaymentError } from "@/api";
import { ApiError } from "@/api/client";
import { toast } from "sonner";
import { useTelegram } from "@/hooks/useTelegram";
import { useZoomradCards } from "@/hooks/useZoomradCards";
import {
  IS_CLICK_DOMAIN,
  IS_PAYMEE_DOMAIN,
  IS_ZOOMRAD_DOMAIN,
  IS_PAYNET_DOMAIN,
} from "@/lib/env";
import { resolveBrandId } from "@/config/uiTokens";
import { openExternalPaymentLink } from "@/lib/payment";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PaymentMethods } from "@/components/PaymentMethods";
import { ZoomradCardCarousel } from "@/components/ZoomradCardCarousel";
import {
  BackHeader,
  Card,
  Tag,
  Chip,
  ProgressBar,
  TextField,
  Button,
  Icon,
  ICONS,
} from "@/components/kit";

/**
 * Fond bloki ko'rsatilmaydigan child ID lari ro'yxati.
 */
export const IGNORE_FOND_CHILD_IDS: number[] = [3792, 3728, 3787, 3794, 3791, 4802];

/**
 * To'lov qanday yakunlangani — muvaffaqiyat ekranidagi matnni belgilaydi.
 *  - "redirect": tashqi checkout ochildi (natija noma'lum — "qayta urinib ko'ring")
 *  - "inApp":    Zoomrad API to'lovni tasdiqladi (aniq muvaffaqiyat)
 */
type PaidVia = null | "redirect" | "inApp";

interface ChildDetailPageProps {
  child: any | null;
  isLoading: boolean;
  onBack: () => void;
  /** false bo'lsa to'lov bo'limi ko'rsatilmaydi (tugatilganlar uchun) */
  showPayment?: boolean;
}

export function ChildDetailPage({
  child,
  isLoading,
  onBack,
  showPayment = true,
}: ChildDetailPageProps) {
  const { t } = useTranslation();
  const { tg, haptic, successHaptic } = useTelegram();
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [paidVia, setPaidVia] = useState<PaidVia>(null);
  const [paying, setPaying] = useState(false);
  // Summa inputiga avtomatik scroll + fokus uchun (sahifaga kirilganda).
  const amountInputRef = useRef<HTMLInputElement>(null);
  const didAutofocusRef = useRef(false);
  const [providerId, setProviderId] = useState(() => {
    // Brand ga mos default provider: payme domenida → payme, boshqalarda → click
    const brand = resolveBrandId();
    const brandMatch = PAYMENT_PROVIDERS.find(
      (p) => p.id === brand && p.available,
    );
    return brandMatch?.id ?? DEFAULT_PROVIDER_ID;
  });

  const activeProvider =
    PAYMENT_PROVIDERS.find((p) => p.id === providerId) ?? PAYMENT_PROVIDERS[0];

  // Zoomrad — yagona in-app provayder: tashqi checkout o'rniga foydalanuvchi
  // o'z kartasini tanlaydi va to'lov shu yerda API orqali bajariladi.
  const isInApp = isInAppProvider(activeProvider);
  const [cardId, setCardId] = useState<string | null>(null);
  const {
    cards,
    isLoading: cardsLoading,
    error: cardsError,
    reload: reloadCards,
  } = useZoomradCards(isInApp && showPayment && paidVia === null);

  // Tanlovni ro'yxat bilan sinxron ushlaydi:
  //  - birinchi karta avtomatik tanlanadi (to'lov ilovalaridagi "asosiy karta"
  //    xatti-harakati) — foydalanuvchi darhol to'lay oladi;
  //  - ro'yxat bo'shab qolsa yoki tanlangan karta o'chirilgan bo'lsa, eskirgan
  //    `cardId` bilan to'lov yuborilmasligi uchun tanlov qayta hisoblanadi.
  useEffect(() => {
    if (cards.length === 0) {
      setCardId((prev) => (prev === null ? prev : null));
      return;
    }
    setCardId((prev) =>
      prev !== null && cards.some((c) => c.cardId === prev) ? prev : cards[0].cardId,
    );
  }, [cards]);

  const handleCardSelect = useCallback(
    (id: string) => {
      haptic("light");
      setCardId(id);
    },
    [haptic],
  );

  const handleAmountSelect = useCallback(
    (amount: number) => {
      haptic("light");
      setSelectedAmount(amount);
      setCustomAmount("");
    },
    [haptic],
  );

  const handleProviderSelect = useCallback(
    (id: ProviderId) => {
      haptic("light");
      setProviderId(id);
    },
    [haptic],
  );

  const handleCustomAmountChange = useCallback((raw: string) => {
    setCustomAmount(parseAmountDigits(raw));
    setSelectedAmount(null);
  }, []);

  // customAmount endi har doim faqat raqamlardan iborat (parseAmountDigits orqali).
  const customValue = customAmount ? parseInt(customAmount, 10) : 0;
  const finalAmount =
    selectedAmount ?? (Number.isNaN(customValue) ? 0 : customValue);
  const amountReady = finalAmount >= MIN_DONATE_AMOUNT;
  // In-app oqimda summadan tashqari karta ham tanlangan bo'lishi shart.
  const cardReady = !isInApp || cardId !== null;
  const canPay = amountReady && cardReady;

  const handlePay = useCallback(async () => {
    if (!child || !canPay || !activeProvider.available || paying) return;

    // 1) In-app (Zoomrad): redirect yo'q — to'lov shu yerda API orqali.
    if (isInAppProvider(activeProvider)) {
      if (!cardId) return;
      setPaying(true);
      try {
        await payZoomradFee({ feeId: child.id, amount: finalAmount, cardId });
        successHaptic();
        setPaidVia("inApp");
      } catch (e) {
        // `ApiError` (server 4xx/5xx, mas. "To'lov amalga oshmadi (Zoomrad
        // kod: 1004)") va `ZoomradPaymentError` uchun xabar allaqachon o'sha
        // joyning o'zida (client.ts / zoomrad.api.ts) toast qilingan.
        //
        // Lekin fetch() javobga yetib bormasdan ham tashlanishi mumkin
        // (CORS, aloqa uzilishi, timeout) — bu holatda hech kim toast
        // ko'rsatmagan bo'ladi va foydalanuvchi hech narsa ko'rmay qoladi.
        // Shu fallback aynan shu bo'shliqni yopadi.
        if (!(e instanceof ApiError) && !(e instanceof ZoomradPaymentError)) {
          toast.error(t("paymentGenericError"));
        }
        // Sahifa holati saqlanadi — foydalanuvchi karta/summani o'zgartirib
        // qayta urinishi mumkin.
      } finally {
        setPaying(false);
      }
      return;
    }

    // 2) Redirect (Click/Payme/Paynet) — umumiy redirect logikasi (@/lib/payment):
    // Telegram ichida ilovani tashqi brauzer orqali ochadi, BazaarView bilan bir xil.
    successHaptic();
    openExternalPaymentLink(
      activeProvider.buildUrl(child, finalAmount),
      activeProvider.id,
      tg,
    );
    setPaidVia("redirect");
  }, [child, canPay, activeProvider, cardId, finalAmount, paying, successHaptic, tg, t]);

  // Sahifa ochilib, ma'lumot yuklangach: summa inputiga avtomatik scroll + fokus
  // (har bola uchun bir marta). Page-transition tugashini kutib, keyin fokuslaymiz.
  useEffect(() => {
    if (didAutofocusRef.current) return;
    if (isLoading || !child || !showPayment || paidVia) return;
    const el = amountInputRef.current;
    if (!el) return;
    didAutofocusRef.current = true;
    const id = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    }, 350);
    return () => window.clearTimeout(id);
  }, [isLoading, child, showPayment, paidVia]);

  if (isLoading || !child) {
    return (
      <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
        <BackHeader onBack={onBack} />
        <LoadingScreen className="min-h-[50vh]" />
      </div>
    );
  }

  const d = getChildDisplay(child);
  const progress = getProgress(d.collected, d.goal);
  const categoryLabel = t(
    `category${d.category.charAt(0).toUpperCase() + d.category.slice(1)}`,
  );
  const remaining = Math.max(0, d.goal - d.collected);
  const showFond = d.fondName && !IGNORE_FOND_CHILD_IDS.includes(child.id);
  const showPaymentSelector =
    !IS_CLICK_DOMAIN && !IS_PAYMEE_DOMAIN && !IS_ZOOMRAD_DOMAIN && !IS_PAYNET_DOMAIN;
  const amountInputValue =
    selectedAmount != null ? formatAmountShort(selectedAmount) : formatAmountInputDisplay(customAmount);
  // CTA matni holatga qarab: summa → karta → to'lash (in-app da qo'shimcha qadam bor)
  const ctaLabel = paying
    ? t("paymentProcessing")
    : !amountReady
      ? t("selectAmount")
      : !cardReady
        ? t("selectCardCta")
        : t("payCta");

  return (
    <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
      <BackHeader
        title={t("myFeesViewDetail")}
        onBack={onBack}
        right={d.urgent ? <Tag tone="dangerSolid">{t("statUrgent")}</Tag> : undefined}
      />

      <div className="mx-auto max-w-lg">
        {/* Hero photo — full-bleed */}
        <div className="relative h-[360px] w-full overflow-hidden" style={{ background: "var(--c-line)" }}>
          {d.image ? (
            <img src={d.image} alt={d.name} className="size-full object-cover object-top" />
          ) : (
            <div className="flex size-full items-center justify-center text-sm" style={{ color: "var(--c-text-disabled)" }}>
              {t("noPhoto")}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 px-4 py-4">
          {/* Summary */}
          <Card className="p-4">
            <div className="flex items-start justify-between gap-2">
              <h1 className="text-xl font-bold leading-tight" style={{ color: "var(--c-text)" }}>
                {d.name}
              </h1>
              <Tag tone="neutral">{categoryLabel}</Tag>
            </div>
            <p className="mt-1 text-sm font-medium" style={{ color: "var(--c-text-muted)" }}>
              {d.age} {t("yearsOld")} • {d.location}
            </p>

            <div className="mt-3 flex items-center gap-2">
              <ProgressBar value={progress} height={6} className="flex-1" />
              <span className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
                {progress}%
              </span>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2">
              {(child.statusId != 4 && child.statusId != 7) &&<Stat label={t("goal") + ": "} value={formatMoney(d.goal)} />}
              <Stat label={t("cardCollected" ) + ": "} value={formatMoney(d.collected)} />
              {showPayment && <Stat label={t("remaining") + ": "} value={formatMoney(remaining)} />}
            </div>
          </Card>

          {/* Fond / Klinika */}
          {(showFond || d.clinicName) && (
            <Card className="flex flex-col gap-3 p-4">
              {showFond && (
                <OrgRow label={t("fund")} name={d.fondName} icon={d.fondIcon} fallback="F" />
              )}
              {showFond && d.clinicName && <div className="h-px" style={{ background: "var(--c-line)" }} />}
              {d.clinicName && (
                <OrgRow label={t("clinic")} name={d.clinicName} icon={d.clinicIcon} fallback="K" />
              )}
            </Card>
          )}

          {/* Tashxis */}
          <Card className="p-4">
            <h2 className="text-base font-semibold" style={{ color: "var(--c-text)" }}>
              {t("comment") + ": "}
            </h2>
            {/* {d.diagnosis && (
              <p className="mt-1 text-sm font-medium" style={{ color: "var(--c-text)" }}>
                {d.diagnosis}
              </p>
            )} */}
            <StoryBlock story={d.story} />
          </Card>

          {/* To'lov bo'limi */}
          {showPayment && !paidVia && (
            <>
              <Card className="p-4">
                <h2 className="mb-3 text-base font-semibold" style={{ color: "var(--c-text)" }}>
                  {t("selectAmountTitle")}
                </h2>
                <TextField
                  ref={amountInputRef}
                  type="text"
                  inputMode="numeric"
                  placeholder={t("amountPlaceholder")}
                  value={amountInputValue}
                  onChange={(e) => handleCustomAmountChange(e.target.value)}
                  trailing={t("sumShort")}
                  aria-label={t("amountLabel")}
                />
                <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1" style={{ scrollbarWidth: "none" }}>
                  {PRESET_AMOUNTS.map((amount) => (
                    <Chip
                      key={amount}
                      selected={selectedAmount === amount}
                      onClick={() => handleAmountSelect(amount)}
                    >
                      {formatAmountCompact(amount)}
                    </Chip>
                  ))}
                </div>
              </Card>

              {/* Zoomrad — to'lov turi emas, KARTA tanlanadi (in-app to'lov).
                  Boshqa hostlarda esa odatdagi to'lov turi selektori. */}
              {isInApp ? (
                <ZoomradCardCarousel
                  cards={cards}
                  value={cardId}
                  onChange={handleCardSelect}
                  isLoading={cardsLoading}
                  error={cardsError}
                  onRetry={reloadCards}
                />
              ) : (
                showPaymentSelector && (
                  <PaymentMethods value={providerId} onChange={handleProviderSelect} />
                )
              )}
            </>
          )}

          {/* Redirect (Click/Payme/Paynet): natija noma'lum — belgi yo'q,
              shuning uchun kichik inline karta yetarli. In-app (Zoomrad)
              uchun to'liq ekranli tasdiq pastda alohida render qilinadi. */}
          {showPayment && paidVia === "redirect" && (
            <Card className="flex flex-col items-center p-5 text-center">
              <p className="text-base font-semibold" style={{ color: "var(--c-text)" }}>
                {t("paymentPageTitle")}
              </p>
              <p className="mt-1 mb-4 text-sm" style={{ color: "var(--c-text-muted)" }}>
                {t("paymentRetryHint")}
              </p>
              <Button
                variant="secondary"
                onClick={() => {
                  setSelectedAmount(null);
                  setCustomAmount("");
                  setPaidVia(null);
                }}
              >
                {t("payAgain")}
              </Button>
            </Card>
          )}
        </div>

        {/* Sticky CTA */}
        {showPayment && !paidVia && (
          <div
            className="sticky bottom-0 z-10 px-4 pt-3"
            style={{
              background: "color-mix(in srgb, var(--app-bg) 92%, transparent)",
              backdropFilter: "blur(8px)",
              borderTop: "1px solid var(--c-line)",
              paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
            }}
          >
            <Button
              size="cta"
              fullWidth
              loading={paying}
              disabled={!canPay}
              onClick={handlePay}
            >
              {ctaLabel}
            </Button>
          </div>
        )}
      </div>

      {/* To'lov API tomonidan tasdiqlangan — mobil to'lov ilovalaridagidek
          to'liq ekranli muvaffaqiyat holati (kichik inline karta emas). */}
      <AnimatePresence>
        {showPayment && paidVia === "inApp" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6"
            style={{
              background: "var(--app-bg)",
              paddingTop: "max(env(safe-area-inset-top), 16px)",
              paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
            }}
          >
            <button
              type="button"
              onClick={onBack}
              aria-label={t("close")}
              className="absolute right-4 top-4 grid size-9 place-items-center rounded-full"
              style={{ background: "var(--c-elevated-2)", color: "var(--c-text-muted)" }}
            >
              <Icon name={ICONS.close} size={20} />
            </button>

            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.05 }}
              className="relative grid place-items-center"
            >
              <motion.span
                className="absolute size-24 rounded-full"
                style={{ background: "var(--c-success)" }}
                initial={{ opacity: 0.35, scale: 1 }}
                animate={{ opacity: 0, scale: 1.7 }}
                transition={{ duration: 1.2, ease: "easeOut", repeat: Infinity, repeatDelay: 0.5 }}
                aria-hidden
              />
              <span
                className="relative grid size-24 place-items-center rounded-full"
                style={{ background: "var(--c-success)", color: "#fff" }}
                aria-hidden
              >
                <Icon name={ICONS.success} size={52} />
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-center"
            >
              <h2 className="text-xl font-bold" style={{ color: "var(--c-text)" }}>
                {t("paymentSuccessTitle")}
              </h2>
              <p className="mt-2 text-sm" style={{ color: "var(--c-text-muted)" }}>
                {t("paymentSuccessHint")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-6 w-full max-w-xs rounded-2xl p-4 text-center"
              style={{ background: "var(--c-elevated-1)", boxShadow: "inset 0 0 0 1px var(--c-line)" }}
            >
              <p className="truncate text-xs font-medium" style={{ color: "var(--c-text-muted)" }}>
                {d.name}
              </p>
              <p className="mt-1 text-2xl font-bold" style={{ color: "var(--c-text)" }}>
                {formatMoney(finalAmount)}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8 flex w-full max-w-xs flex-col gap-2"
            >
              <Button size="cta" fullWidth onClick={onBack}>
                {t("back")}
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setSelectedAmount(null);
                  setCustomAmount("");
                  setPaidVia(null);
                }}
              >
                {t("payAgain")}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Summary stat ── */
function Stat({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-xs font-medium" style={{ color: "var(--c-text-muted)" }}>
        {label}
      </p>
      <p
        className="mt-0.5 truncate text-xs font-semibold"
        style={{ color: accent ? "var(--primary)" : "var(--c-text)" }}
      >
        {value}
      </p>
    </div>
  );
}

/* ── Fond/Klinika row ── */
function OrgRow({ label, name, icon, fallback }: { label: string; name: string; icon?: string; fallback: string }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-full"
        style={{ boxShadow: "inset 0 0 0 1px var(--c-line)", background: "var(--c-surface)" }}
      >
        {icon ? (
          <img src={icon} alt="" className="size-full object-cover" loading="lazy" />
        ) : (
          <span className="text-[11px]" style={{ color: "var(--c-text-tertiary)" }}>{fallback}</span>
        )}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium" style={{ color: "var(--c-text-muted)" }}>{label}</p>
        <p className="truncate text-sm font-semibold" style={{ color: "var(--c-text)" }}>{name}</p>
      </div>
    </div>
  );
}

/* ── Story block with expand/collapse ── */
function StoryBlock({ story }: { story: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setOverflows(el.scrollHeight > el.clientHeight + 2);
  }, [story]);

  if (!story) return null;

  return (
    <div className="mt-2">
      <p
        ref={textRef}
        className={`text-sm leading-relaxed whitespace-pre-line ${!expanded ? "line-clamp-4" : ""}`}
        style={{ color: "var(--c-text-muted)" }}
      >
        {story}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-sm font-medium"
          style={{ color: "var(--primary)" }}
        >
          {expanded ? t("showLess") : t("showMore")}
        </button>
      )}
    </div>
  );
}
